import type { FilterCondition, FilterState } from '../filter-panel';

export interface TimelineSearchParams {
  q: string;
  query_by: string;
  filter_by: string;
  additionalTextIncludes: string[];
}

function normalizeConditionValues(
  conditions: FilterCondition[],
  field: FilterCondition['field'],
  lowerCase = false,
): string[] {
  const values = conditions
    .filter((condition) => condition.field === field)
    .map((condition) => condition.value.trim())
    .filter(Boolean);
  const normalized = lowerCase ? values.map((value) => value.toLowerCase()) : values;
  return Array.from(new Set(normalized));
}

function toFilterString(value: string): string {
  return `\`${value.replaceAll('`', '\\`')}\``;
}

function toQueryTerm(value: string): string {
  const escaped = value.replaceAll('"', '\\"');
  return /\s/.test(escaped) ? `"${escaped}"` : escaped;
}

function toEpochStart(date: string): number {
  return Math.floor(new Date(`${date}T00:00:00.000Z`).getTime() / 1000);
}

function toEpochEnd(date: string): number {
  return Math.floor(new Date(`${date}T23:59:59.999Z`).getTime() / 1000);
}

export function buildSearchParams(caseId: string, filters: FilterState): TimelineSearchParams {
  const includeSenders = normalizeConditionValues(filters.include, 'sender', true);
  const excludeSenders = normalizeConditionValues(filters.exclude, 'sender', true);
  const includeTexts = normalizeConditionValues(filters.include, 'text');
  const excludeTexts = normalizeConditionValues(filters.exclude, 'text');

  const filterParts: string[] = [`caseId:=${toFilterString(caseId)}`];

  if (filters.types.size > 0) {
    const typeValues = Array.from(filters.types).map((type) => toFilterString(type));
    filterParts.push(`type:[${typeValues.join(',')}]`);
  }

  if (filters.hasAttachments) {
    filterParts.push('hasAttachments:=true');
  }

  if (filters.dateFrom) filterParts.push(`timestamp:>=${toEpochStart(filters.dateFrom)}`);
  if (filters.dateTo) filterParts.push(`timestamp:<=${toEpochEnd(filters.dateTo)}`);

  for (const sender of includeSenders) filterParts.push(`fromEmail:=${toFilterString(sender)}`);
  for (const sender of excludeSenders) filterParts.push(`fromEmail:!=${toFilterString(sender)}`);

  const primaryTextInclude = includeTexts[0];
  const additionalTextIncludes = includeTexts.slice(1);
  const negativeTerms = excludeTexts.map((term) => `-${toQueryTerm(term)}`);
  const baseQuery = primaryTextInclude ? toQueryTerm(primaryTextInclude) : '*';
  const q = [baseQuery, ...negativeTerms].join(' ').trim();

  return {
    q,
    query_by: 'title,contentPreview',
    filter_by: filterParts.join('&&'),
    additionalTextIncludes,
  };
}
